// Claude: Admin Departments page — flat list CRUD for buyer departments 16-Apr-2026
import React, { useEffect, useState } from "react";
import {
  Box, Button, Flex, Text, Input, Spinner, VStack, HStack, IconButton,
} from "@chakra-ui/react";
import { MdOutlineAdd, MdCheck, MdClose, MdEdit, MdDelete } from "react-icons/md";
import { DepartmentsAPI } from "../../Apis/departmentsAPI";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { errorHandler } from "../../Utils/handler";

const Departments = () => {
  const { success, error } = useToastWrapper();
  const [departments, setDepartments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Claude: add mode state 16-Apr-2026
  const [addName, setAddName] = useState("");
  const [adding, setAdding] = useState(false);
  const [showAddInput, setShowAddInput] = useState(false);

  // Claude: edit mode state — one row at a time 16-Apr-2026
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await DepartmentsAPI.getAll();
      const list: any[] = Array.isArray(res) ? res : (res?.data ?? []);
      setDepartments(list);
    } catch (err: any) {
      error(errorHandler(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleAdd = async () => {
    const name = addName.trim();
    if (!name) { error("Please enter a department name."); return; }
    // Claude: client-side duplicate check before API call 16-Apr-2026
    if (departments.some((d) => d.name.toLowerCase() === name.toLowerCase())) {
      error("A department with this name already exists."); return;
    }
    setAdding(true);
    try {
      await DepartmentsAPI.create({ name });
      success("Department added.");
      setAddName("");
      setShowAddInput(false);
      await fetchAll();
    } catch (err: any) {
      error(errorHandler(err));
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (dept: any) => {
    setEditingId(dept._id);
    setEditName(dept.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
  };

  const handleEdit = async (id: string) => {
    const name = editName.trim();
    if (!name) { error("Name cannot be empty."); return; }
    setEditSaving(true);
    try {
      await DepartmentsAPI.update(id, { name });
      success("Department updated.");
      cancelEdit();
      await fetchAll();
    } catch (err: any) {
      error(errorHandler(err));
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete department "${name}"?`)) return;
    try {
      await DepartmentsAPI.remove(id);
      success("Department deleted.");
      await fetchAll();
    } catch (err: any) {
      error(errorHandler(err));
    }
  };

  return (
    <Box p={6} maxW="600px">
      <Flex alignItems="center" justifyContent="space-between" mb={5}>
        <Text fontSize="xl" fontWeight={700} color="#0E122F">Departments</Text>
        <Button
          size="sm" leftIcon={<MdOutlineAdd size={16} />}
          bg="#0E122F" color="white" _hover={{ bg: "#159300" }}
          borderRadius="8px"
          onClick={() => { setShowAddInput(true); setAddName(""); }}
        >
          Add Department
        </Button>
      </Flex>

      {/* Claude: inline add input 16-Apr-2026 */}
      {showAddInput && (
        <Flex mb={4} gap={2} alignItems="center">
          <Input
            placeholder="Department name"
            value={addName}
            onChange={(e) => setAddName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); if (e.key === "Escape") setShowAddInput(false); }}
            size="sm" borderRadius="8px" focusBorderColor="#7C66FF"
            autoFocus
          />
          <IconButton aria-label="Confirm" icon={<MdCheck size={16} />} size="sm"
            colorScheme="green" borderRadius="8px"
            isLoading={adding} onClick={handleAdd} />
          <IconButton aria-label="Cancel" icon={<MdClose size={16} />} size="sm"
            variant="outline" borderRadius="8px"
            onClick={() => { setShowAddInput(false); setAddName(""); }} />
        </Flex>
      )}

      {loading ? (
        <Flex justifyContent="center" mt={10}><Spinner color="#0E122F" /></Flex>
      ) : departments.length === 0 ? (
        <Text fontSize="sm" color="gray.400" mt={4}>No departments yet. Click "Add Department" to create one.</Text>
      ) : (
        <VStack spacing={0} alignItems="stretch" border="1px solid" borderColor="gray.200" borderRadius="10px" overflow="hidden">
          {departments.map((dept, idx) => (
            <Box
              key={dept._id}
              px={4} py={3}
              bg={idx % 2 === 0 ? "white" : "gray.50"}
              borderBottom={idx < departments.length - 1 ? "1px solid" : "none"}
              borderColor="gray.200"
            >
              {editingId === dept._id ? (
                // Claude: inline edit row 16-Apr-2026
                <Flex gap={2} alignItems="center">
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleEdit(dept._id); if (e.key === "Escape") cancelEdit(); }}
                    size="sm" borderRadius="8px" focusBorderColor="#7C66FF"
                    autoFocus flex={1}
                  />
                  <IconButton aria-label="Save" icon={<MdCheck size={16} />} size="sm"
                    colorScheme="green" borderRadius="8px"
                    isLoading={editSaving} onClick={() => handleEdit(dept._id)} />
                  <IconButton aria-label="Cancel" icon={<MdClose size={16} />} size="sm"
                    variant="outline" borderRadius="8px" onClick={cancelEdit} />
                </Flex>
              ) : (
                <Flex alignItems="center" justifyContent="space-between">
                  <Text fontSize="sm" color="#0E122F">{dept.name}</Text>
                  <HStack spacing={1}>
                    <IconButton aria-label="Edit" icon={<MdEdit size={15} />} size="xs"
                      variant="ghost" color="gray.500" _hover={{ color: "#0E122F" }}
                      onClick={() => startEdit(dept)} />
                    <IconButton aria-label="Delete" icon={<MdDelete size={15} />} size="xs"
                      variant="ghost" color="gray.400" _hover={{ color: "#C62828" }}
                      onClick={() => handleDelete(dept._id, dept.name)} />
                  </HStack>
                </Flex>
              )}
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  );
};

export default Departments;
